import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { PiArrowRightBold, PiBookOpenLight, PiEyeBold, PiUsersLight } from "react-icons/pi";
import client from "../../api/client";

export default function InstructorCourses() {
  const [assignments, setAssignments] = useState([]);
  const [allCohorts, setAllCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("assigned"); // assigned, other

  useEffect(() => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;

    (async () => {
      try {
        const [assignRes, cohortsRes] = await Promise.all([
          client.get("/instructor/courses"),
          client.get("/cohorts")
        ]);
        setAssignments(assignRes.data || []);
        setAllCohorts(cohortsRes.data?.cohorts || []);
      } catch (err) {
        toast.error("Failed to load classrooms.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isAssigned = (cohortId, courseUuid) => {
    return assignments.some(
      (a) => a.cohort_id === cohortId && a.course?.uuid === courseUuid
    );
  };

  // Build the list of other classrooms
  const otherClassrooms = [];
  allCohorts.forEach((cohort) => {
    (cohort.courses || []).forEach((course) => {
      if (!isAssigned(cohort.id, course.uuid)) {
        otherClassrooms.push({
          id: `${cohort.id}-${course.uuid}`,
          cohort: { id: cohort.id, name: cohort.name, is_open: true },
          course,
        });
      }
    });
  });

  return (
    <div className="py-8 px-4 md:px-8 max-w-6xl mx-auto">
      {/* Title */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Classrooms Directory</h1>
        <p className="text-sm text-gray-500 mt-1">
          Browse all courses and cohorts. You have full edit access to your assigned classrooms and read-only access to others.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-8">
        <button
          onClick={() => setActiveTab("assigned")}
          className={`px-5 py-3 text-sm font-semibold border-b-2 transition-all flex items-center gap-2 ${
            activeTab === "assigned"
              ? "border-[#F05A19] text-[#F05A19]"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
          }`}
        >
          <PiBookOpenLight size={18} />
          My Assigned Classrooms
          <span className="ml-1.5 bg-orange-100 text-orange-800 text-xs font-semibold px-2 py-0.5 rounded-full">
            {assignments.length}
          </span>
        </button>
        <button
          onClick={() => setActiveTab("other")}
          className={`px-5 py-3 text-sm font-semibold border-b-2 transition-all flex items-center gap-2 ${
            activeTab === "other"
              ? "border-[#F05A19] text-[#F05A19]"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
          }`}
        >
          <PiUsersLight size={18} />
          Browse Other Classrooms
          <span className="ml-1.5 bg-gray-100 text-gray-800 text-xs font-semibold px-2 py-0.5 rounded-full">
            {otherClassrooms.length}
          </span>
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-150 p-6 animate-pulse space-y-4">
              <div className="h-32 bg-gray-100 rounded-xl" />
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-3 bg-gray-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : activeTab === "assigned" ? (
        assignments.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 py-16 text-center shadow-sm">
            <p className="text-4xl mb-3">👨‍🏫</p>
            <h3 className="font-semibold text-gray-700 text-base">No Assigned Classrooms</h3>
            <p className="text-sm text-gray-400 mt-1">
              You are not currently assigned to teach any cohorts.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {assignments.map(({ id, cohort, course }) => {
              if (!course || !cohort) return null;
              const imageUrl = course.image_url || "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&auto=format&fit=crop&q=80";
              return (
                <div
                  key={id}
                  className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 duration-200 flex flex-col justify-between"
                >
                  <div className="h-36 relative bg-slate-900">
                    <img src={imageUrl} alt={course.title} className="w-full h-full object-cover opacity-80" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
                    <span className="absolute top-4 right-4 bg-emerald-500 text-white text-[10px] uppercase font-bold px-2 py-0.5 rounded-md shadow-sm">
                      Assigned
                    </span>
                  </div>
                  <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                    <div className="space-y-1.5">
                      <h3 className="text-base font-bold text-gray-800 line-clamp-1">{course.title}</h3>
                      <p className="text-xs text-gray-500 flex items-center gap-1.5">
                        <PiUsersLight size={16} /> Cohort: <strong>{cohort.name}</strong>
                      </p>
                    </div>
                    <Link
                      to={`/dashboard/course/${course.uuid}?cohort_id=${cohort.id}`}
                      className="flex items-center justify-center gap-2 w-full bg-[#F05A19] hover:bg-[#d14d14] text-white text-xs font-bold rounded-xl py-3 transition-colors shadow-md shadow-orange-100"
                    >
                      Manage Classroom
                      <PiArrowRightBold size={14} />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : otherClassrooms.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 py-16 text-center shadow-sm">
          <p className="text-4xl mb-3">🏫</p>
          <h3 className="font-semibold text-gray-700 text-base">No other classrooms</h3>
          <p className="text-sm text-gray-400 mt-1">There are no other active cohorts in the academy.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {otherClassrooms.map(({ id, cohort, course }) => {
            const imageUrl = course.image_url || "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=600&auto=format&fit=crop&q=80";
            return (
              <div
                key={id}
                className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 duration-200 flex flex-col justify-between"
              >
                <div className="h-36 relative bg-slate-900">
                  <img src={imageUrl} alt={course.title} className="w-full h-full object-cover opacity-60" />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 to-transparent" />
                  <span className="absolute top-4 right-4 bg-slate-700 text-white text-[10px] uppercase font-bold px-2 py-0.5 rounded-md shadow-sm">
                    Read-Only
                  </span>
                </div>
                <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
                  <div className="space-y-1.5">
                    <h3 className="text-base font-bold text-gray-800 line-clamp-1">{course.title}</h3>
                    <p className="text-xs text-gray-500 flex items-center gap-1.5">
                      <PiUsersLight size={16} /> Cohort: <strong>{cohort.name}</strong>
                    </p>
                  </div>
                  <Link
                    to={`/dashboard/course/${course.uuid}?cohort_id=${cohort.id}`}
                    className="flex items-center justify-center gap-2 w-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl py-3 transition-colors"
                  >
                    <PiEyeBold size={14} />
                    View Classroom
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
